import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, Chip, IconButton, Button, TextField,
  MenuItem, Select, FormControl, InputLabel, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import { fetchData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import { printDocument, field, row, schoolHeader } from "../../utils/printUtils";

const STATUS_COLORS = {
  pending:           { bg: "#fff3e0", color: "#e65100", label: "Pending" },
  eligible:          { bg: "#e8f5e9", color: "#2e7d32", label: "Eligible" },
  ineligible:        { bg: "#fce4ec", color: "#c62828", label: "Ineligible" },
  payment_completed: { bg: "#e3f2fd", color: "#1565c0", label: "Payment Done" },
  approved:          { bg: "#e8f5e9", color: "#1b5e20", label: "Approved" },
  rejected:          { bg: "#fce4ec", color: "#b71c1c", label: "Rejected" },
};

function StatusChip({ status }) {
  const s = STATUS_COLORS[status] || { bg: "#f5f5f5", color: "#555", label: status };
  return (
    <Chip label={s.label} size="small"
      sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 600, fontSize: 11, borderRadius: "16px" }} />
  );
}

export default function AdminBiliteracy() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("admin/biliteracy-mgmt/list");
      setRows(res?.data || []);
    } catch {
      // use mock data if API not ready
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCertificate = () => {
    const s = selectedRow;
    printDocument(`Biliteracy Certificate — ${s?.firstName}`, `
      ${schoolHeader('Certificate of Seal of Biliteracy', '')}
      <div style="text-align:center;margin:30px 0">
        <p style="font-size:16px;color:#555">This is to certify that</p>
        <p style="font-size:28px;font-weight:900;color:#1F3864;margin:12px 0">${s?.firstName || ''}</p>
        <p style="font-size:14px;color:#555">has successfully demonstrated biliteracy proficiency in <strong>Tamil and English</strong></p>
        <p style="font-size:14px;color:#555;margin-top:8px">Grade: <strong>${s?.gradeSection || '—'}</strong></p>
        ${row(field('Registration No', s?.registrationNo), field('Overall Grade', s?.overallGrade))}
        <div style="margin-top:40px;display:flex;justify-content:center;gap:80px">
          <div style="text-align:center">
            <div style="border-top:2px solid #333;width:160px;margin:0 auto 6px"></div>
            <p style="font-size:12px">Principal's Signature</p>
          </div>
          <div style="text-align:center">
            <div style="border-top:2px solid #333;width:160px;margin:0 auto 6px"></div>
            <p style="font-size:12px">Date: ${new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>
    `);
  };

  const handleAction = async (id, status) => {
    try {
      await putData(`admin/biliteracy-mgmt/update-status/${id}`, { status });
      setToast({ open: true, severity: "success", message: `Status updated to ${status}` });
      load();
      setDetailOpen(false);
    } catch {
      setToast({ open: true, severity: "error", message: "Update failed." });
    }
  };

  const filtered = rows.filter(r => {
    const matchSearch = !search ||
      (r.firstName || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.registrationNo || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.gradeSection || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const stats = {
    total:   rows.length,
    pending: rows.filter(r => r.status === "pending").length,
    eligible:rows.filter(r => r.status === "eligible").length,
    payment: rows.filter(r => r.status === "payment_completed").length,
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Toast open={toast.open} handleClose={() => setToast(t => ({ ...t, open: false }))}
        severity={toast.severity} message={toast.message} />

      <Typography variant="h5" sx={{ fontWeight: "bold", color: "#1a1a2e", mb: 0.5 }}>
        Seal of Biliteracy Management
      </Typography>
      <Typography sx={{ color: "rgba(0,0,0,0.5)", fontSize: 13, mb: 3 }}>
        Review and manage student biliteracy enrollment applications
      </Typography>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: "Total Applications", value: stats.total,   bg: "#e3f2fd", color: "#1565c0" },
          { label: "Pending Review",     value: stats.pending,  bg: "#fff3e0", color: "#e65100" },
          { label: "Eligible",           value: stats.eligible, bg: "#e8f5e9", color: "#2e7d32" },
          { label: "Payment Completed",  value: stats.payment,  bg: "#f3e5f5", color: "#244C77" },
        ].map((k, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper sx={{ p: 2.5, borderRadius: 3, backgroundColor: k.bg }}>
              <Typography sx={{ fontSize: 28, fontWeight: "bold", color: k.color }}>{k.value}</Typography>
              <Typography sx={{ fontSize: 13, color: k.color, opacity: 0.8 }}>{k.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, borderRadius: 2, mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <TextField size="small" placeholder="Search by name, reg no, grade..."
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "#aaa" }} /> }}
          sx={{ minWidth: 280 }} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="eligible">Eligible</MenuItem>
            <MenuItem value="ineligible">Ineligible</MenuItem>
            <MenuItem value="payment_completed">Payment Done</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1F3864" }}>
                {["#", "First Name", "Reg No", "Grade & Section", "Class", "Overall Grade",
                  "Parent Name", "Applied Date", "Status", "Actions"].map(h => (
                  <TableCell key={h} sx={{ color: "#fff", fontWeight: 700, fontSize: 12, py: 1.5 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4, color: "#888" }}>
                    No biliteracy applications found
                  </TableCell>
                </TableRow>
              ) : filtered.map((r, idx) => (
                <TableRow key={r.id || idx}
                  sx={{ "&:hover": { backgroundColor: "#f9f9f9" },
                        backgroundColor: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                  <TableCell sx={{ fontSize: 12 }}>{idx + 1}</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{r.firstName || "—"}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.registrationNo || "—"}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.gradeSection || "—"}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.class || "—"}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.overallGrade || "—"}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.parentName || "—"}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell><StatusChip status={r.status} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => { setSelectedRow(r); setDetailOpen(true); }}>
                          <VisibilityIcon sx={{ fontSize: 16, color: "#244C77" }} />
                        </IconButton>
                      </Tooltip>
                      {r.status === "pending" && (
                        <>
                          <Tooltip title="Mark Eligible">
                            <IconButton size="small" onClick={() => handleAction(r.id, "eligible")}>
                              <CheckCircleIcon sx={{ fontSize: 16, color: "#2e7d32" }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Mark Ineligible">
                            <IconButton size="small" onClick={() => handleAction(r.id, "ineligible")}>
                              <CancelIcon sx={{ fontSize: 16, color: "#c62828" }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: "linear-gradient(135deg,#4A7BA8,#244C77)", color: "#fff", fontWeight: 700 }}>
          Biliteracy Application Detail
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedRow && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {[
                ["First Name", selectedRow.firstName],
                ["Registration No", selectedRow.registrationNo],
                ["Grade & Section", selectedRow.gradeSection],
                ["Class", selectedRow.class],
                ["Overall Grade", selectedRow.overallGrade],
                ["Applied Date", selectedRow.createdAt ? new Date(selectedRow.createdAt).toLocaleDateString() : "—"],
                ["Parent Name", selectedRow.parentName],
                ["Parent Email", selectedRow.parentEmail],
                ["Parent Phone", selectedRow.parentPhone],
                ["Status", selectedRow.status],
              ].map(([label, value]) => (
                <Grid item xs={6} key={label}>
                  <Typography sx={{ fontSize: 11, color: "#888", mb: 0.3 }}>{label}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    {label === "Status" ? <StatusChip status={value} /> : (value || "—")}
                  </Typography>
                </Grid>
              ))}
              {selectedRow.googleFormText && (
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: 11, color: "#888", mb: 0.3 }}>Google Form Response</Typography>
                  <Paper sx={{ p: 1.5, backgroundColor: "#f8f8f8", borderRadius: 1 }}>
                    <Typography sx={{ fontSize: 12 }}>{selectedRow.googleFormText}</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          {selectedRow?.status === "pending" && (
            <>
              <Button variant="contained" color="success" size="small"
                onClick={() => handleAction(selectedRow.id, "eligible")}>
                Mark Eligible
              </Button>
              <Button variant="contained" color="error" size="small"
                onClick={() => handleAction(selectedRow.id, "ineligible")}>
                Mark Ineligible
              </Button>
            </>
          )}
          {selectedRow?.status === "eligible" && (
            <Button variant="contained" size="small"
              sx={{ background: "linear-gradient(135deg,#4A7BA8,#244C77)" }}
              onClick={() => handleAction(selectedRow.id, "approved")}>
              Mark Payment Complete
            </Button>
          )}
          {selectedRow?.status === "approved" && (
            <Button variant="outlined" color="success" size="small" onClick={handleCertificate}
              startIcon={<PrintIcon />}>
              Certificate
            </Button>
          )}
          <Button onClick={() => setDetailOpen(false)} size="small">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
